/**
 * Payroll — stage 1: the people payroll is run for (spec §18.3).
 *
 * WHAT IS HERE, AND WHAT IS NOT. Employees, each with whether an SSN is on file
 * and the W-4 in effect, and the pay runs (stage 3). Pay stubs, deposits and the
 * worksheets are stage 4 and have no placeholders here: a list of things that do
 * nothing yet is clutter.
 *
 * A RUN IS READ FROM ITS OWN PAGE. This list carries what tells one run from
 * another -- who, when, how much, and what became of it -- and nothing else.
 *
 * ADDING AN EMPLOYEE ASKS WHETHER THEY ARE THE OWNER-OFFICER, with neither
 * answer chosen (see EmployeeForm), and then opens the new employee, where the
 * W-4 is recorded.
 *
 * THE SSN IS TYPED ONCE AND NEVER SHOWN BACK. The list says only whether one is
 * on file, because that is all the API says.
 */

import { offersForm940, useBusiness, useCan } from '../auth/AuthProvider.js';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmployeeForm } from '../components/EmployeeForm.js';
import { ApiError, api, type CreateEmployeeBody, type EmployeeSummary, type PayrollRunSummary } from '../api.js';
import { employeeName } from '../lib/labels.js';
import { formatCents } from '../lib/money.js';
import { runStatusLabel } from './PayRunPage.js';

export function PayrollPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const runsPayroll = can('run_payroll');
  const business = useBusiness();
  const files940 = offersForm940(business);
  // Only an S corporation has an owner-officer (0083); an unrecorded type shows it, as before.
  const hasOwnerOfficer = business == null || business.taxStatus == null || business.taxStatus === 's_corporation';
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeSummary[] | null>(null);
  const [runs, setRuns] = useState<PayrollRunSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listEmployees(), api.listPayrollRuns()])
      .then(([people, paid]) => {
        if (cancelled) return;
        setEmployees(people.employees);
        setRuns(paid.runs);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the employees.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function add(body: CreateEmployeeBody) {
    setBusy(true);
    setError(null);
    try {
      const { employee } = await api.createEmployee(body);
      navigate(`/payroll/employees/${employee.id}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add the employee.');
      setBusy(false);
    }
  }

  return (
    <section className="payroll">
      <div className="row payroll-head">
        <h1>Payroll</h1>
        {!adding && runsPayroll && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setAdding(true);
            }}
          >
            Add Employee
          </button>
        )}
      </div>
      {/*
        §18.7's deposits and reports, one link each from the screen payroll
        starts on. Links, not sections: each is its own screen, and a summary
        here would be a second place to read the same figures. A line of their
        own since 2026-09-24: eight of them beside the heading squeezed every
        one onto two lines, which read as a narrow screen.
      */}
      <div className="row payroll-links">
        <Link to="/payroll/deposits">Tax Deposits</Link>
        <Link to="/payroll/register">Payroll Register</Link>
        <Link to="/payroll/year-to-date">Year to Date</Link>
        <Link to="/payroll/forms/941">Form 941 Worksheet</Link>
        <Link to="/payroll/forms/twc">TWC Quarterly Worksheet</Link>
        <Link to="/payroll/forms/w2">W-2/W-3 Worksheet</Link>
        {/* A 501(c)(3) owes no FUTA, so files no Form 940 (0078). */}
        {files940 && <Link to="/payroll/forms/940">Form 940 Worksheet</Link>}
        <Link to="/payroll/forms/1099-nec">Form 1099-NEC Worksheet</Link>
      </div>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {adding && (
        <EmployeeForm
          label="New Employee"
          withSsn
          submitLabel="Save Employee"
          busy={busy}
          onSubmit={(body) => void add(body)}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
        />
      )}

      {loadError ? (
        <p role="alert" className="error">
          {loadError}
        </p>
      ) : employees === null ? (
        <p className="muted">Loading…</p>
      ) : employees.length === 0 ? (
        <p className="muted">No employees yet.</p>
      ) : (
        <table className="lines" aria-label="Employees">
          <thead>
            <tr>
              <th>Name</th>
              <th>Hire Date</th>
              {hasOwnerOfficer && <th>Owner-Officer</th>}
              <th>Officer</th>
              <th>SSN</th>
              <th>W-4</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td>
                  <Link to={`/payroll/employees/${employee.id}`}>{employeeName(employee)}</Link>
                </td>
                <td className="date-cell">{employee.hireDate}</td>
                {hasOwnerOfficer && <td>{employee.isOwnerOfficer ? 'Yes' : 'No'}</td>}
                <td>{employee.isOfficer ? 'Yes' : 'No'}</td>
                <td>{employee.hasSsn ? 'On File' : 'Not on File'}</td>
                <td>
                  {employee.currentW4 ? `In Effect Since ${employee.currentW4.effectiveOn}` : 'None in Effect'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loadError && (
        <section className="settings-section" aria-labelledby="payroll-runs">
          <div className="row payroll-head">
            <h2 id="payroll-runs">Pay Runs</h2>
            {runsPayroll && (
              <button type="button" onClick={() => navigate('/payroll/runs/new')}>
                New Pay Run
              </button>
            )}
          </div>
          {runs === null ? (
            <p className="muted">Loading…</p>
          ) : runs.length === 0 ? (
            <p className="muted">No pay runs yet.</p>
          ) : (
            <table className="lines" aria-label="Pay Runs">
              <thead>
                <tr>
                  <th>Pay Date</th>
                  <th>Employee</th>
                  <th className="amount">Gross</th>
                  <th className="amount">Net</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td>
                      <Link to={`/payroll/runs/${run.id}`}>{run.payDate}</Link>
                    </td>
                    <td>{run.employeeName}</td>
                    <td className="amount">{formatCents(run.grossPayCents)}</td>
                    <td className="amount">{formatCents(run.netPayCents)}</td>
                    <td>
                      {runStatusLabel(run)}
                      {run.awaitingBankDebit && ' · Waiting for the Bank Debit'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  );
}
